import { useParams, Link } from 'react-router-dom';
import { ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { getFeatureBySlug, FEATURES } from '../../data/features';

export default function FeaturePage() {
  const { slug } = useParams<{ slug: string }>();
  const feature = slug ? getFeatureBySlug(slug) : undefined;

  if (!feature) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <section className="pt-32 pb-20 lg:pt-44 lg:pb-28">
          <div className="container-custom text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Feature Not Found</h1>
            <p className="text-gray-600 mb-8">
              We could not find this feature. Browse all features below.
            </p>
            <Link to="/features" className="btn-primary">
              View All Features
            </Link>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  const related = feature.relatedSlugs
    .map((s) => FEATURES.find((f) => f.slug === s))
    .filter(Boolean);

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 overflow-hidden">
        <div className="absolute inset-0 gradient-bg -z-10" />
        <div className="container-custom">
          <div className="max-w-4xl mx-auto text-center">
            <Link
              to="/features"
              className="inline-flex items-center gap-2 text-sm font-medium text-gray-500 hover:text-gray-900 transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              All Features
            </Link>

            <div className="text-5xl mb-6">{feature.icon}</div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-gray-900 mb-6">
              {feature.name}
            </h1>

            <p className="text-lg sm:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
              {feature.tagline}
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://app.ihirefast.com"
                className="btn-primary text-base px-8 py-4 w-full sm:w-auto group"
              >
                Try iHireFast Free
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="btn-secondary text-base px-8 py-4 w-full sm:w-auto"
              >
                Talk to Sales
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Description */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-lg text-gray-600 leading-relaxed">{feature.description}</p>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
              Benefits
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Why you will love {feature.name}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {feature.benefits.map((benefit, i) => (
              <div key={i} className="card p-6">
                <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center mb-4">
                  <CheckCircle className="w-6 h-6 text-primary-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{benefit.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block px-4 py-1.5 bg-primary-100 text-primary-700 rounded-full text-sm font-medium mb-4">
              How It Works
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Get started in minutes
            </h2>
          </div>

          <div className="max-w-3xl mx-auto space-y-6">
            {feature.howItWorks.map((step, i) => (
              <div key={i} className="flex gap-4 items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center text-white font-bold text-sm">
                  {i + 1}
                </div>
                <div className="pt-2">
                  <p className="text-gray-700 leading-relaxed">{step}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Screenshot Placeholder */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-primary-100 to-primary-50 rounded-2xl border-2 border-dashed border-primary-200 p-16 text-center">
              <div className="text-4xl mb-4">{feature.icon}</div>
              <h3 className="text-xl font-semibold text-primary-700 mb-2">{feature.name}</h3>
              <p className="text-primary-600 text-sm">Feature screenshot coming soon</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }}
          />
        </div>
        <div className="container-custom relative">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
              Try {feature.name} today
            </h2>
            <p className="text-lg text-primary-100 mb-10 max-w-xl mx-auto">
              Start for free. No credit card required. See how {feature.name.toLowerCase()} can
              transform your hiring process.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://app.ihirefast.com"
                className="group inline-flex items-center gap-2 px-8 py-4 bg-white text-primary-700 rounded-lg font-semibold hover:bg-primary-50 transition-colors shadow-lg"
              >
                Get Started Free
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-transparent border-2 border-white/30 text-white rounded-lg font-semibold hover:bg-white/10 transition-colors"
              >
                Schedule a Demo
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Related Features */}
      {related.length > 0 && (
        <section className="py-12 bg-gray-50 border-t border-gray-200">
          <div className="container-custom">
            <div className="text-center mb-8">
              <h3 className="text-lg font-semibold text-gray-900">Related features</h3>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
              {related.map((rel) =>
                rel ? (
                  <Link
                    key={rel.slug}
                    to={`/features/${rel.slug}`}
                    className="card p-4 group hover:shadow-md transition-all duration-300 text-center"
                  >
                    <div className="text-2xl mb-2">{rel.icon}</div>
                    <h4 className="text-sm font-semibold text-gray-900 group-hover:text-primary-600 transition-colors">
                      {rel.name}
                    </h4>
                  </Link>
                ) : null,
              )}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
